import { useCallback } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import NotesTable from "@/features/global/components/NotesTable";
import { getNoteParentLink } from "@/features/global/components/NotesTable/noteHelpers";
import Banner from "@/components/Banner";
import { GET_STUDENT_ENROLLMENT_QUERY, type GetStudentEnrollmentQuery } from "./queries";
import { PERSON_NOTES_QUERY } from "@/pages/people/[pgaId]/person/notes/queries";
import { usePersonNotesAccess } from "@/pages/people/[pgaId]/person/_usePersonNotesAccess";
import { Student } from "../../types";
import { CRMNote, NoteNotableValues } from "@/types";

export default function StudentNotesPage() {
  const { student } = useOutletContext<{ student: Student }>();
  const { universityId } = useParams();
  const { loaded, canViewPersonNotes } = usePersonNotesAccess();

  const universityUuid = student.university?.id;

  const { data: enrollmentData, loading: enrollmentLoading } = useQuery<GetStudentEnrollmentQuery>(
    GET_STUDENT_ENROLLMENT_QUERY,
    {
      variables: { personId: student.id, universityId: universityUuid },
      skip: !loaded || !canViewPersonNotes || !student.id || !universityUuid,
      errorPolicy: "all"
    }
  );

  const enrollmentId = enrollmentData?.person?.universityCohortEnrollment?.id;

  const hasEnrollment = !!enrollmentId;

  const getParentLink = useCallback(
    (note: CRMNote): string | null =>
      getNoteParentLink(note, { pgaId: student.pgaId, universityLegacyId: universityId }),
    [student.pgaId, universityId]
  );

  const noteEditing =
    hasEnrollment && enrollmentId
      ? {
          enabled: true,
          notableId: enrollmentId,
          notableType: NoteNotableValues.UNIVERSITY_COHORT_ENROLLMENT,
          refetchQueries: [PERSON_NOTES_QUERY],
          pgaId: student.pgaId,
          objectType: "student"
        }
      : undefined;

  if (!loaded || !canViewPersonNotes) {
    return null;
  }

  return (
    <div className="relative">
      {!hasEnrollment && !enrollmentLoading && (
        <div className="mt-8">
          <Banner
            variant="warn"
            description="Adding new notes is only available for students associated with a cohort"
            inline={true}
          />
        </div>
      )}
      <NotesTable
        variant="top-level"
        notesQuery={PERSON_NOTES_QUERY}
        queryVariables={{ pgaId: student.pgaId }}
        notableTypeFilter={[NoteNotableValues.UNIVERSITY_COHORT_ENROLLMENT, NoteNotableValues.UNIVERSITY_PROBATION]}
        tableHeading="Student Notes"
        tableBody="View all enrollment and probation notes for this student"
        getParentLink={getParentLink}
        noteEditing={noteEditing}
        addNoteVariant="filled"
      />
    </div>
  );
}
